import React, { useState, useEffect } from 'react';
import { VStack, Text } from 'native-base';

// Redux
import {
  useAppDispatch,
  openModal,
  useUpdateUserDataMutation,
  useUploadUserPhotoMutation,
} from '@breakroom-nx/redux';

// Assets & Icons
import { theme } from '@breakroom-nx/assets';

// Components
import { PrimaryButton, TextField, TextView, ModifyProfilePhoto } from '../../';

// Classes & Types
import { User } from '@breakroom-nx/classes';
import { InputType, Status } from '@breakroom-nx/helpers';

import * as Yup from 'yup';
import { Formik, ErrorMessage } from 'formik';
import { QueryStatus } from '@reduxjs/toolkit/dist/query';

const usernameRegExp = /\w/;
const nameRegExp = /^[A-Za-z\s-']+$/;
const SettingsScheme = Yup.object().shape({
  name: Yup.string()
    .matches(
      nameRegExp,
      'Only letters, spaces, hyphens and apostrophes allowed'
    )
    .max(50, 'Too Long!')
    .optional(),
  username: Yup.string()
    .matches(
      usernameRegExp,
      "Only letters, numbers and '_' underscores allowed"
    )
    .max(32, 'Too Long!')
    .required('Choose a username'),
  bio: Yup.string().max(200, 'Maximum of 200 characters').optional(),
});

interface SettingsFormValues {
  name: string;
  username: string;
  bio: string;
}

/* eslint-disable-next-line */
export interface UserPersonalSettingsProps {
  user: User;
}

export function UserPersonalSettings({ user }: UserPersonalSettingsProps) {
  const [image, setImage] = useState<any | undefined>(undefined);
  const [isChanged, setIsChanged] = useState(false); // Track if any field has changed

  const [updateUserData, updatedData] = useUpdateUserDataMutation();
  const [uploadUserPhoto, uploadedPhotoState] = useUploadUserPhotoMutation();

  // Redux
  const dispatch = useAppDispatch();

  const initialValues: SettingsFormValues = {
    name: user?.name ?? '',
    username: user?.username ?? '',
    bio: user?.bio ?? '',
  };

  const imageUpdated = (image: string) => {
    // console.log(`Image Updated: ${image}`);
    setImage(image);
  };

  return (
    <VStack
      backgroundColor={theme.colors.surface[100]}
      borderColor={theme.colors.surface[300]}
      borderWidth={1}
      p={8}
      borderRadius={10}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={SettingsScheme}
        onSubmit={async (values, { setSubmitting }) => {
          // console.log(
          //   `Values [Image: ${image} Name: ${values.name} Username: ${values.username} Bio: ${values.bio}]`
          // );

          if (image) {
            await uploadUserPhoto(image).catch((err) => {
              dispatch(
                openModal({
                  modalType: 'Alert',
                  modalProps: {
                    status: Status.error,
                    title: 'Error uploading your photo',
                    description: err,
                  },
                })
              );
            });
          }

          await updateUserData({
            username: values.username,
            name: values.name,
            bio: values.bio,
          })
            .then(() => {
              dispatch(
                openModal({
                  modalType: 'Alert',
                  modalProps: {
                    status: Status.success,
                    title: 'Profile Updated!',
                  },
                })
              );
            })
            .catch(() => {
              dispatch(
                openModal({
                  modalType: 'Alert',
                  modalProps: {
                    status: Status.error,
                    title: 'There was an error updating your profile',
                    description: 'Please try again.',
                  },
                })
              );
            });
        }}
      >
        {({ values, isSubmitting, handleChange, handleBlur, handleSubmit }) => {
          // Track changes to the form fields and image
          useEffect(() => {
            const hasChanges =
              values.name !== initialValues.name ||
              values.username !== initialValues.username ||
              values.bio !== initialValues.bio ||
              image !== undefined;
            setIsChanged(hasChanges);
          }, [values, image]);

          return (
            <VStack
              space={5}
              alignItems={'stretch'}
              justifyContent={'flex-start'}
            >
              <ModifyProfilePhoto
                showPoints={false}
                onPhotoSelected={imageUpdated}
              />

              <VStack space={8} mt={8} justifyContent={'flex-start'} flex={1}>
                <Text
                  fontFamily={'heading'}
                  fontSize={26}
                  color={'white'}
                  fontWeight={600}
                >
                  Personal
                </Text>
                <VStack space={2}>
                  <Text
                    fontFamily={'heading'}
                    fontSize={'lg'}
                    color={theme.colors.purple[200]}
                    fontWeight={600}
                  >
                    Name
                  </Text>
                  <TextField
                    disabled={isSubmitting}
                    type={InputType.Name}
                    value={values.name}
                    onChangeText={handleChange('name')}
                    onBlur={handleBlur('name')}
                    isRequired={true}
                    placeholder={'Full Name'}
                    inactiveTextColor={'surface.500'}
                  />
                  <Text variant={'error'}>
                    <ErrorMessage name="name" />
                  </Text>
                </VStack>
                <VStack space={2}>
                  <Text
                    fontFamily={'heading'}
                    fontSize={'lg'}
                    color={theme.colors.purple[200]}
                    fontWeight={600}
                  >
                    Username
                  </Text>
                  <TextField
                    disabled={isSubmitting}
                    type={InputType.Username}
                    value={values.username}
                    onChangeText={handleChange('username')}
                    onBlur={handleBlur('username')}
                    isRequired={true}
                    placeholder={'Username'}
                    inactiveTextColor={'surface.500'}
                  />
                  <Text variant={'error'}>
                    <ErrorMessage name="username" />
                  </Text>
                </VStack>
                <VStack space={2}>
                  <Text
                    fontFamily={'heading'}
                    fontSize={'lg'}
                    color={theme.colors.purple[200]}
                    fontWeight={600}
                  >
                    Bio
                  </Text>
                  <TextView
                    disabled={isSubmitting}
                    type={InputType.Text}
                    value={values.bio}
                    onChangeText={handleChange('bio')}
                    onBlur={handleBlur('bio')}
                    isRequired={true}
                    height={150}
                    inactiveTextColor={'surface.500'}
                    placeholder={'Write a bio'}
                  />
                  <Text variant={'error'}>
                    <ErrorMessage name="bio" />
                  </Text>
                </VStack>
                <PrimaryButton
                  disabled={
                    !isChanged || updatedData.status === QueryStatus.pending
                  }
                  isLoading={updatedData.status === QueryStatus.pending}
                  text="Save Changes"
                  onPress={handleSubmit}
                />
              </VStack>
            </VStack>
          );
        }}
      </Formik>
    </VStack>
  );
}

export default UserPersonalSettings;
